import assert from 'assert'
import { MintType } from '@rarible/sdk/build/types/nft/mint/prepare'
import { sortBy } from 'lodash-es'
import { update } from './augmintables'
import { getWeb3, getWeb3UserOp, getAccount } from './account'
import { isAddress, isSameAddress, containsIPFSCID, isDigits } from '~/utils/validation'
import { extractAddress, sleep } from '~/utils/helpers'
import { RARIBLE_COLLECTION_ADDR, RARIBLE_ROYALTIES, RARIBLE_FEE_ADDRESS, RARIBLE_ORIGIN_FEE, RARIBLE_KEY, USDC_ADDRESS as currency } from '~/utils/constants'
import store from '~/store'

assert(isAddress(RARIBLE_COLLECTION_ADDR), `Invalid RARIBLE_COLLECTION_ADDR`)
assert(isAddress(RARIBLE_FEE_ADDRESS), `Invalid RARIBLE_FEE_ADDRESS`)

export const COLLECTION_ADDRESS = RARIBLE_COLLECTION_ADDR
export const ROYALTIES = RARIBLE_ROYALTIES * 100
export const FEE_ADDRESS = RARIBLE_FEE_ADDRESS
export const ORIGIN_FEE = RARIBLE_ORIGIN_FEE * 100

export const getRaribleSDK = async (config = { sponsored: false }) => {
  const { createRaribleSdk } = await import('@rarible/sdk')
  const web3 = config.sponsored ? getWeb3UserOp() : getWeb3()
  return createRaribleSdk(web3, 'prod', { apiKey: RARIBLE_KEY })
}

export const mint = async (uri,creatorAddr,artistName,creatorRoyaltiesPercent,supply = 1) => {
  const mintData = getMintData(uri, creatorAddr, artistName, creatorRoyaltiesPercent, supply)
  console.info({ mintData })
  const raribleSDK = await getRaribleSDK()
  const { transaction, itemId } = await raribleSDK.nft.mint(mintData)
  const mintResult = await wait(transaction)
  console.info({ mintResult })
  if (mintResult.type === MintType.OFF_CHAIN) return mintResult.itemId
  return itemId
}

export const getMintData = (uri, creatorAddr, artistName, creatorRoyaltiesPercent, supply = 1) => {
  if (!isAddress(creatorAddr)) throw new Error(`Invalid creator address ${creatorAddr}`)
  if (!isDigits(creatorRoyaltiesPercent)) throw new Error(`Invalid creator royalties ${creatorRoyaltiesPercent}`)
  if (!/^http/.test(uri)) throw new Error(`Invalid URI. Must be a public IPFS uri ${uri}`)
  if (!containsIPFSCID(uri)) throw new Error(`Invalid URI. Must be a valid IPFS uri ${uri}`)

  const lazyMint = false
  const creators = [{ account: toAddress(creatorAddr), value: 10000 }]
  const royalties = [{ account: toAddress(FEE_ADDRESS), value: ROYALTIES }]
  const collectionId = toBaseAddress(COLLECTION_ADDRESS)

  if (creatorRoyaltiesPercent > 0)
    royalties.push({ account: toAddress(creatorAddr), value: creatorRoyaltiesPercent * 100 })

  return { collectionId, uri, royalties, creators, lazyMint, supply, artistName }
}

export const sell = async (itemId, amount, price) => {
  const raribleSDK = await getRaribleSDK()
  let orderId;
  orderId = await raribleSDK.order.sell(getSellData(itemId, amount, price))
  try {
    // This throws an error event the listing is posted. It breaks some validation rules on orderId
  } catch (e) {
    const message = e.toString();
    if (message.startsWith('Error: Not an OrderId:')) {
      orderId = message.replace('Error: Not an OrderId: ', '')
    } else {
      console.error(e)
    }
  }
  console.info({ orderId })
  return orderId
}

export const getSellData = (itemId, amount, price) => {
  const originFees = [{ account: toAddress(FEE_ADDRESS), value: ORIGIN_FEE }]
  const lazy = false
  return { itemId, amount, price, currency, originFees, lazy }
}

export const buy = async (order, amount = 1) => {
  const raribleSDK = await getRaribleSDK({sponsored: true})
  const tx = await raribleSDK.order.buy(getBuyData(order, amount))
  console.info({ tx })
  return wait(tx)
}

export const getBuyData = (order, amount = 1) => {
  const orderId = order?.id || order
  return { orderId, amount, currency }
}

export const transfer = async (itemId, sendToAddress, amount) => {
  const raribleSDK = await getRaribleSDK()
  const tx = await raribleSDK.nft.transfer(getTransferData(itemId, sendToAddress, amount))
  console.info({ tx })
  return wait(tx)
}

export const getTransferData = (itemId, sendToAddress, amount) => {
  const to = toAddress(sendToAddress)
  return { itemId, to, amount }
}

export const burn = async (itemId, amount) => {
  const raribleSDK = await getRaribleSDK()
  const tx = await raribleSDK.nft.burn(getBurnData(itemId, amount))
  console.info({ tx })
  if (tx) return wait(tx)
}

export const getBurnData = (itemId, amount) => {
  // maintain pattern for consistency
  return { itemId, amount }
}

export const cancelOrder = async (order) => {
  const raribleSDK = await getRaribleSDK()
  const sellCountBefore = await getAccountSalesCount();
  const cancelTx = await raribleSDK.order.cancel(getCancelOrderData(order))
  while(sellCountBefore === await getAccountSalesCount()) {
    await sleep(1000)
  }
  console.info({ cancelTx })
  return wait(cancelTx)
}

export const getCancelOrderData = (order) => {
  return { orderId: order.id }
}

export const updateWhenSellersReady = async ( itemId, sellerAddress, waitForNotSeller = false) => {
  const raribleSDK = await getRaribleSDK()
  const status = 'ACTIVE'
  try {
    for (let attempt = 0; attempt < 30; attempt++) {
      const { orders } = await raribleSDK.apis.order.getSellOrdersByItem({ itemId, status }).catch(console.warn)
      const addressIsSeller = orders
        .map(({ maker }) => extractAddress(maker))
        .find((seller) => isSameAddress(seller, sellerAddress))

      if (!addressIsSeller && waitForNotSeller) return update(itemId)
      else if (addressIsSeller && !waitForNotSeller) return update(itemId)
      await sleep(2000)
    }
    console.error('timed out waiting for sellers to update', itemId)
  } catch (e) {
    console.error('problem waiting for sellers to update', e)
  }
}

export const getSellsByItem = async (itemId, continuation) => {
  const raribleSDK = await getRaribleSDK()
  const status = 'ACTIVE'
  const { orders } = await raribleSDK.apis.order.getSellOrdersByItem({ itemId, status, continuation })
  if (orders.find((o) => o.pending?.length)) {
    await sleep(2000)
    return getSellsByItem(itemId, continuation)
  }
  return sortBy(orders, (o) => parseInt(o.makePrice))
}

export const getSellsByMaker = async (makerAddress) => {
  const raribleSDK = await getRaribleSDK()
  const status = 'ACTIVE'
  const maker = toAddress(makerAddress)
  let { orders, continuation } = await raribleSDK.apis.order.getSellOrdersByMaker({ maker, status })
  while (continuation) {
    console.info('loading next page of orders')
    const { orders: nextOrders, continuation: nextContinuation } = await raribleSDK.apis.order.getSellOrdersByMaker({ maker, status, continuation })
    orders = orders.concat(nextOrders)
    continuation = nextContinuation
  }
  const collectionOrders = orders.filter((o) => isSameAddress(extractAddress(o.make?.type?.contract), COLLECTION_ADDRESS))
  return collectionOrders
}

function getAccountSalesCount() {
  return getSellsByMaker(getAccount()).then((sells) => sells.length)
}

export const isTokenId = (str) => {
  return /^[0-9]{1,78}$/.test(`${str}`)
}

export const isItemId = (str) => {
  try {
    const [network, addr, tokenId] = str.split(':')
    return network === 'BASE' && isAddress(addr) && isTokenId(tokenId)
  } catch (e) {
    console.error(e)
    return false
  }
}

export const toTokenId = (str) => {
  let _tokenId = `${str}`
  if (_tokenId.includes(':')) _tokenId = _tokenId.split(':').pop()
  return isTokenId(_tokenId) ? _tokenId : null
}

function toAddress(addr) {
  if (!isAddress(addr)) throw new Error(`Invalid address ${addr}`)
  return `ETHEREUM:${addr}`
}

function toBaseAddress(addr) {
  if (!isAddress(addr)) throw new Error(`Invalid address ${addr}`)
  return `BASE:${addr}`
}

const wait = (tx) => {
  store.commit('setLoadingMessageOverlay', 'Waiting for transaction…')
  return tx.wait().finally(() => store.commit('setLoadingMessageOverlay', null))
}
